import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton } from "primereact/skeleton";
import { useAuth } from "../../context/authContext";
import { useSolicitacaoDetalhe } from "../../hooks/solicitacoes/useSolicitacaoDetalhe";
import { useEncaminhamento } from "../../hooks/solicitacoes/useEncaminhamento";
import Button from "../../componentes/button/button";
import AcaoConcluidaScreen from "../../components/solicitacoes/Shared/AcaoConcluidaScreen";
import CabecalhoSolicitacao from "../../components/solicitacoes/Shared/CabecalhoSolicitacao";
import CancelarSolicitacaoModal from "../../components/solicitacoes/Shared/CancelarSolicitacaoModal";
import TransferirTarefaSidebar from "../../components/solicitacoes/Shared/TransferirTarefaSidebar";
import EncaminhamentoSidebar from "../../components/solicitacoes/Shared/EncaminhamentoSidebar";
import EtapaProgress from "../../components/solicitacoes/Shared/EtapaProgress";
import Timeline from "../../components/solicitacoes/Timeline/Timeline";
import ComentarioForm from "../../components/solicitacoes/Comentarios/ComentarioForm";
import ComentarioList from "../../components/solicitacoes/Comentarios/ComentarioList";
import AnexoList from "../../components/solicitacoes/Anexos/AnexoList";
import UploadArea from "../../components/solicitacoes/Anexos/UploadArea";
import { processoInfo, etapaComponentInfo } from "../../utils/solicitacoes/processoRegistry";
import { prioridadeInfo } from "../../utils/solicitacoes/statusConfig";
import { mapearRateioParaPdf } from "../../utils/solicitacoes/orcamento";

// Reproduz o layout final da página (cabeçalho, progresso, etapa, sidebar) com placeholders,
// para a transição de loading -> dados parecer uma revelação do próprio conteúdo, não uma troca de tela.
function EsqueletoDetalheSolicitacao() {
    return (
        <main className="px-4 py-6 fade-in">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <Skeleton width="6rem" height="1.25rem" />
                    <div className="flex items-center gap-2">
                        <Skeleton width="5.5rem" height="1.75rem" borderRadius="9999px" />
                        <Skeleton width="7rem" height="1.75rem" borderRadius="9999px" />
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-3">
                    <Skeleton width="14rem" height="1.5rem" />
                    <Skeleton width="10rem" height="1rem" />
                    <div className="flex gap-4 flex-wrap mt-1">
                        <Skeleton width="8rem" height="0.9rem" />
                        <Skeleton width="8rem" height="0.9rem" />
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card p-4">
                    <Skeleton width="100%" height="2.5rem" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 flex flex-col gap-4">
                        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-3">
                            <Skeleton width="8rem" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="1rem" />
                            <Skeleton width="100%" height="1rem" />
                            <Skeleton width="60%" height="1rem" />
                            <Skeleton width="8rem" height="2.25rem" className="mt-2" />
                        </div>

                        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-3">
                            <Skeleton width="9rem" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="3rem" />
                        </div>
                    </div>

                    <div className="lg:col-span-1 flex flex-col gap-4">
                        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-3">
                            <Skeleton width="6rem" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="5rem" />
                        </div>

                        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-3">
                            <Skeleton width="8rem" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="1rem" />
                            <Skeleton width="100%" height="1rem" />
                            <Skeleton width="80%" height="1rem" />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default function DetalheSolicitacao() {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        solicitacao, detalheProcesso, timeline, anexos, primeiraCargaCompleta, acaoEmExecucao,
        recarregar, executarAcao, cancelarSolicitacao, assumirTarefa, transferirTarefa, comentar, anexar, excluirAnexo, substituirAnexo, executores
    } = useSolicitacaoDetalhe(id);

    const { user } = useAuth();
    const [modalCancelarAberto, setModalCancelarAberto] = useState(false);
    const [modalTransferirAberto, setModalTransferirAberto] = useState(false);
    const [assumindo, setAssumindo] = useState(false);
    // Preenchido só após uma movimentação bem-sucedida — dispara a tela de confirmação em vez de
    // deixar o usuário na mesma página/redirecionar automaticamente (nunca em caso de erro).
    const [confirmacao, setConfirmacao] = useState(null);
    // Rateio ainda sendo digitado na etapa atual (ex.: Recebimento de Compras), reportado pelo
    // próprio componente de etapa via onRateioChange — sem isso, "Adicionar Rateio" ao assinar um
    // anexo só apareceria depois de já ter enviado a etapa (quando detalheProcesso.rateioOrcamentario
    // finalmente é atualizado), tarde demais pra ser útil. null = nenhuma etapa reporta rateio em
    // edição agora, cai no rateio já persistido. Limpo sempre que a etapa muda, pra não vazar um
    // rateio de uma etapa anterior já concluída.
    const [rateioEmEdicao, setRateioEmEdicao] = useState(null);

    useEffect(() => {
        document.title = solicitacao ? `MyDash | ${solicitacao.numero}` : "MyDash | Solicitação";
    }, [solicitacao]);

    useEffect(() => {
        setRateioEmEdicao(null);
    }, [solicitacao?.etapaAtual]);

    // Compras expõe o rateio em "rateioOrcamentario", Despesa em "rateio" — mesmo shape de item; o
    // rateio em edição usa "valor" (RateioOrcamentarioEditor), o já persistido usa "valorRateado".
    const rateioDetalhado = rateioEmEdicao ?? (detalheProcesso?.rateioOrcamentario ?? detalheProcesso?.rateio ?? []);
    const rateioParaPdf = mapearRateioParaPdf(rateioDetalhado, rateioEmEdicao ? "valor" : "valorRateado");

    // Dados de forma de pagamento já preenchidos na própria solicitação (Compras e Despesa
    // guardam os mesmos campos) — pré-preenche o carimbo de "Forma de Pagamento" ao assinar um
    // anexo, sem obrigar o usuário a digitar de novo o que já foi informado.
    const formaPagamento = {
        tipoPagamento: detalheProcesso?.tipoPagamento,
        tipoConta: detalheProcesso?.tipoConta,
        agencia: detalheProcesso?.agencia,
        agenciaDigito: detalheProcesso?.agenciaDigito,
        conta: detalheProcesso?.conta,
        contaDigito: detalheProcesso?.contaDigito,
        chavePix: detalheProcesso?.chavePix,
    };

    const anexoObrigatorioNaEtapa = !!solicitacao?.anexoObrigatorioNaEtapa;
    const temAnexo = anexos.length > 0;
    const bloqueadoPorAnexo = anexoObrigatorioNaEtapa && !temAnexo;

    // Processo finalizado ou cancelado não aceita mais anexos novos (a assinatura, que só troca o
    // arquivo de um anexo já existente, continua liberada).
    const processoFinalizado = solicitacao?.status === "FINALIZADA";
    const processoCancelado = solicitacao?.status === "CANCELADA";
    const podeAnexar = !processoFinalizado && !processoCancelado;

    const encaminhamento = useEncaminhamento();

    // Ponto único de encaminhamento: os campos da etapa já validaram (cada componente de etapa
    // garante isso antes de chamar esta função) — aqui só resta perguntar, via Sidebar, quem
    // recebe a próxima etapa antes de realmente enviar ao backend.
    function encaminhar(acao) {
        return encaminhamento.abrirParaAcao(solicitacao, acao);
    }

    // Ponto único acionado ao final de QUALQUER movimentação bem-sucedida (via executarAcao/
    // cancelarSolicitacao ou, para as etapas de Compras que preenchem dados próprios, diretamente
    // pelas próprias etapas) — mostra a tela de confirmação com a etapa anterior (capturada antes
    // da chamada) e a solicitação já no estado pós-transição (dados vindos do backend).
    function onAcaoConcluida(acao, solicitacaoAtualizada) {
        setConfirmacao({ acao, etapaAnterior: solicitacao.etapaAtual, solicitacaoAtualizada });
        recarregar();
    }

    async function onExecutarAcao(acao, extras) {
        if (bloqueadoPorAnexo) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return;
        }
        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar(acao);
        } catch {
            // Usuário cancelou o encaminhamento — não é um erro, só desiste da ação.
            return;
        }
        return executarAcao(acao, { ...extras, usuarioEscolhidoId })
            .then(dados => onAcaoConcluida(acao, dados))
            .catch(() => { });
    }

    const anexosRef = useRef(null);
    const comentariosRef = useRef(null);
    const comentariosCount = timeline.filter(i => i.tipo === "COMENTARIO").length;

    function scrollPara(ref) {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    const [enviandoAnexo, setEnviandoAnexo] = useState(false);

    async function onAnexar(arquivos, descricao) {
        setEnviandoAnexo(true);
        for (const arquivo of arquivos) {
            try {
                await anexar(arquivo, descricao);
            } catch (error) {
                console.error("Erro ao anexar arquivo", error);
                toast.error(`Erro ao anexar "${arquivo.name}"`);
            }
        }
        setEnviandoAnexo(false);
    }

    if (!primeiraCargaCompleta) {
        return <EsqueletoDetalheSolicitacao />;
    }

    if (!solicitacao) {
        return (
            <main className="px-4 py-6 text-center text-muted-foreground">
                <i className="fa fa-exclamation-circle text-2xl mb-2 block" />
                Solicitação não encontrada ou sem acesso.
            </main>
        );
    }

    if (confirmacao) {
        return (
            <AcaoConcluidaScreen
                acao={confirmacao.acao}
                etapaAnterior={confirmacao.etapaAnterior}
                solicitacao={confirmacao.solicitacaoAtualizada}
                onVerSolicitacao={() => setConfirmacao(null)}
            />
        );
    }

    const podeCancelar = user?.id === solicitacao.solicitanteId
        && !["FINALIZADA", "CANCELADA", "ELIMINADA"].includes(solicitacao.status);
    // Mesma condição de quem pode agir na etapa (backend): quem está com a tarefa pode repassá-la.
    const podeTransferir = solicitacao.responsavelAtualId === user.id;


    function onAssumirTarefa() {
        setAssumindo(true);
        assumirTarefa()
            .then(() => {
                toast.success("Tarefa assumida com sucesso.");
                recarregar();
            })
            // Erro já vira toast pelo interceptor global do axios (api.jsx) — só evita unhandled rejection aqui.
            .catch(() => {})
            .finally(() => setAssumindo(false));
    }

    const processo = processoInfo(solicitacao.tipoProcesso);
    const etapaInfo = solicitacao.etapaAtual ? etapaComponentInfo(solicitacao.tipoProcesso, solicitacao.etapaAtual) : null;
    const EtapaComponent = etapaInfo?.component;
    const DetalheComponent = processo?.detalheComponent;

    const camposExtras = [];
    if (detalheProcesso?.filialNome) camposExtras.push({ label: "Filial", valor: detalheProcesso.filialNome });
    if (detalheProcesso?.prioridade) {
        camposExtras.push({ label: "Prioridade", valor: prioridadeInfo(detalheProcesso.prioridade)?.label || detalheProcesso.prioridade });
    }
    
    return (
        <main className="px-4 py-6 fade-in">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-fit"
                    >
                        <i className="fa fa-arrow-left" /> Voltar
                    </button>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => scrollPara(anexosRef)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-muted hover:bg-primary/10 hover:text-primary px-3 py-1.5 text-xs font-semibold text-foreground/80 transition-colors duration-150"
                        >
                            <i className="fa fa-paperclip" /> {anexos.length} {anexos.length === 1 ? "Anexo" : "Anexos"}
                        </button>
                        <button
                            type="button"
                            onClick={() => scrollPara(comentariosRef)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-muted hover:bg-primary/10 hover:text-primary px-3 py-1.5 text-xs font-semibold text-foreground/80 transition-colors duration-150"
                        >
                            <i className="fa fa-comment" /> {comentariosCount} {comentariosCount === 1 ? "Comentário" : "Comentários"}
                        </button>
                    </div>
                </div>

                <CabecalhoSolicitacao
                    solicitacao={solicitacao}
                    camposExtras={camposExtras}
                    acoes={(solicitacao.podeAssumir || podeTransferir || podeCancelar) && (
                        <div className="flex items-center gap-3 flex-wrap">
                            {solicitacao.podeAssumir && (
                                <Button
                                    variant="blue"
                                    iconLeft="fa fa-hand-paper"
                                    label="Assumir tarefa"
                                    loading={assumindo}
                                    onClick={onAssumirTarefa}
                                />
                            )}
                            {podeTransferir && (
                                <Button
                                    variant="outline"
                                    iconLeft="fa fa-share"
                                    label="Transferir"
                                    onClick={() => setModalTransferirAberto(true)}
                                />
                            )}
                            {podeCancelar && (
                                <button
                                    className="itransition-colors duration-150 text-xs text-muted-foreground hover:opacity-70 flex items-center gap-1.5"
                                    onClick={() => setModalCancelarAberto(true)}
                                >
                                    <i className="fa fa-ban" /> Cancelar
                                </button>
                            )}
                        </div>
                    )}
                />

                {processo?.etapas && (
                    <div className="bg-card border border-border rounded-lg shadow-card p-4">
                        <EtapaProgress
                            etapas={processo.etapasParaExibir ? processo.etapasParaExibir(detalheProcesso) : processo.etapas}
                            executores={executores}
                            etapaAtualCodigo={solicitacao.etapaAtual}
                            status={solicitacao.status}
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 flex flex-col gap-4">
                        {EtapaComponent && solicitacao.responsavelAtualId === (user.id) && (
                            <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6">
                                <h2 className="font-display text-base font-semibold text-foreground mb-4">Etapa atual</h2>
                                <EtapaComponent
                                    solicitacao={solicitacao}
                                    detalheProcesso={detalheProcesso}
                                    acoes={solicitacao.acoesDisponiveis}
                                    acaoEmExecucao={acaoEmExecucao}
                                    onExecutarAcao={onExecutarAcao}
                                    onDepoisDeAgir={recarregar}
                                    onAcaoConcluida={onAcaoConcluida}
                                    encaminhar={encaminhar}
                                    anexoObrigatorioNaEtapa={anexoObrigatorioNaEtapa}
                                    temAnexo={temAnexo}
                                    anexar={anexar}
                                    anexos={anexos}
                                    onRateioChange={setRateioEmEdicao}
                                    {...etapaInfo.props}
                                />
                            </div>
                        )}

                        {DetalheComponent && <DetalheComponent detalheProcesso={detalheProcesso} anexos={anexos} solicitacao={solicitacao} />}

                        <div ref={comentariosRef} className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 scroll-mt-4">
                            <h2 className="font-display text-base font-semibold text-foreground mb-4">Comentários</h2>
                            <div className="flex flex-col gap-4">
                                <ComentarioList itens={timeline} />
                                <ComentarioForm onEnviar={comentar} />
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-1 flex flex-col gap-4">
                        <div ref={anexosRef} className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 scroll-mt-4">
                            <div className="flex items-center gap-2 mb-4 flex-wrap">
                                <h2 className="font-display text-base font-semibold text-foreground">Anexos</h2>
                                {anexoObrigatorioNaEtapa && (
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${temAnexo ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                                        Anexo obrigatório
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col gap-4">
                                {podeAnexar ? (
                                    <UploadArea onArquivoSelecionado={onAnexar} enviando={enviandoAnexo} sugestoes={processo.sugestoes} />
                                ) : (
                                    <p className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">
                                        <i className="fa fa-lock mr-1.5" />
                                        Não é possível anexar arquivos em um processo {processoFinalizado ? "finalizado" : "cancelado"}.
                                    </p>
                                )}
                                {bloqueadoPorAnexo && (
                                    <p className="text-xs text-destructive">
                                        <i className="fa fa-exclamation-circle mr-1" />
                                        É necessário anexar pelo menos um documento para concluir esta etapa.
                                    </p>
                                )}
                                <AnexoList
                                    anexos={anexos}
                                    onAssinar={substituirAnexo}
                                    onExcluir={excluirAnexo}
                                    usuarioLogadoId={user?.id}
                                    etapaAtual={solicitacao.etapaAtual}
                                    rateioItens={rateioParaPdf}
                                    rateioItensDetalhados={rateioDetalhado}
                                    formaPagamento={formaPagamento}
                                />
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 lg:sticky lg:top-4">
                            <h2 className="font-display text-base font-semibold text-foreground mb-4">Linha do tempo</h2>
                            <div className="max-h-[450px] overflow-y-auto">
                                <Timeline itens={timeline} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EncaminhamentoSidebar
                isOpen={encaminhamento.aberto}
                carregando={encaminhamento.carregando}
                preview={encaminhamento.preview}
                contexto={encaminhamento.contexto}
                onCancelar={encaminhamento.cancelar}
                onConfirmar={encaminhamento.confirmar}
            />

            <CancelarSolicitacaoModal
                visible={modalCancelarAberto}
                solicitacao={solicitacao}
                onFechar={() => setModalCancelarAberto(false)}
                onConfirmar={motivo => cancelarSolicitacao(motivo).then(dados => {
                    setModalCancelarAberto(false);
                    onAcaoConcluida("CANCELAR", dados);
                })}
            />

            <TransferirTarefaSidebar
                isOpen={modalTransferirAberto}
                solicitacao={solicitacao}
                onFechar={() => setModalTransferirAberto(false)}
                onConfirmar={(novoUsuarioId, motivo) => transferirTarefa(novoUsuarioId, motivo).then(dados => {
                    setModalTransferirAberto(false);
                    toast.success("Tarefa transferida com sucesso.");
                    recarregar();
                    return dados;
                })}
            />
        </main>
    );
}
